import { assertNever } from '@/coreDomainsModule/assertNever';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IAnnounce, IAnnounceDaily } from '@/coreDomainsModule/entities/forecast';
import { filterStringNumberUndefinedToString } from '@/coreDomainsModule/extends/string';
import {
  DifferenceConfirmationCodeAndName,
  ExcelOutputRow,
  ExcelRow,
  ExcelTranslateError,
  InputExcelType,
  TranslateExcelBusiness,
} from '@/coreDomainsModule/translateExcel';

/**
 * 入力エクセルの基本情報のパラメータが正しいかを確認する
 * 前提として、バリデーション対象の取引とは「置場名」「商品CD」「容量」「仕入先名」が空でないことが前提（それ以外はバリデーション対象外なのでこのメソッドは使わない）
 * @param business - 取引情報
 * @param excelData - エクセルデータ
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateInputFoundationParameters = (business: IBusiness, excelData: ExcelRow, inputErrorObj: ExcelTranslateError[]) => {
  const fileName = excelData?.[Object.keys(excelData)?.[0]]?.fileName; // 基本的にfileNameは同じはずなので最初の1つ目を取得
  const sheetName = excelData?.[Object.keys(excelData)?.[0]]?.sheetName; // 基本的にfileNameは同じはずなので最初の1つ目を取得
  if (fileName === undefined || sheetName === undefined) {
    inputErrorObj.push({
      key: business?.businessId,
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: '',
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: '',
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '',
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ファイル名、又はシート名が読み込めませんでした。フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  const propertyNames = ['置場CD', '商品CD', '容量', '送り先CD', '売上先CD', '仕入先CD'];

  const errorMessages: [string, string][] = [];

  for (const propertyName of propertyNames) {
    const cellNumber = excelData[propertyName]?.address;

    // NOTE: 送り先名、売上先名は必須ではないため、空の場合はエラーにしない
    if (cellNumber === undefined && propertyName !== '送り先CD' && propertyName !== '売上先CD') {
      errorMessages.push([`${propertyName}が読み込めませんでした。フォーマットを再度確認し、修正してください。`, '']);
      continue;
    }

    const cellNum = cellNumber?.toString() ?? '';

    if (propertyName === '置場CD') {
      if (business?.warehouseNumber === '') {
        errorMessages.push(['置場CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.warehouseNumber.length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '商品CD') {
      if (business?.businessNumber === '') {
        errorMessages.push(['商品CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.businessNumber.length > 40) {
        errorMessages.push(['商品CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '仕入先CD') {
      if (business?.supplierNumber === '') {
        errorMessages.push(['仕入先CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.supplierNumber.length > 40) {
        errorMessages.push(['仕入先CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '売上先CD') {
      if ((business?.customerNumber ?? '').length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '送り先CD') {
      if ((business?.salesDestinationNumber ?? '').length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '容量') {
      if (business?.package === '') {
        errorMessages.push(['容量が空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (isNaN(Number(business?.package))) {
        errorMessages.push(['容量が数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    errorMessages.forEach(([errorMessage, errorCellNumber]) => {
      inputErrorObj.push({
        key: business?.businessId,
        errCode: 'InputExcelInCorrectType',
        errorFileName: {
          inputFileName: fileName.toString(),
          registerFileName: '',
        },
        preCellNumber: '',
        errorSheetName: {
          inputSheetName: sheetName.toString(),
          registerSheetName: '',
        },
        errorCellNumber: {
          inputCellNumber: errorCellNumber,
          registerCellNumber: '',
        },
        value: undefined,
        errorString: errorMessage,
      });
    });
  }
};

/**
 * 入力エクセルの基本情報のパラメータが正しいかを確認する（TODO: 読み替え対応以降はこちらを使う、上記のメソッドは削除予定）
 * 前提として、バリデーション対象の取引とは「置場名」「商品CD」「容量」「仕入先名」が空でないことが前提（それ以外はバリデーション対象外なのでこのメソッドは使わない）
 * @param business - 取引情報
 * @param excelData - エクセルデータ
 * @param propertyNames - プロパティ名の情報（読みかえ用）
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const newValidateInputFoundationParameters = (
  business: IBusiness,
  excelData: ExcelRow,
  propertyNames: Record<string, string>,
  inputErrorObj: ExcelTranslateError[]
) => {
  const fileName = excelData?.[Object.keys(excelData)?.[0]]?.fileName; // 基本的にfileNameは同じはずなので最初の1つ目を取得
  const sheetName = excelData?.[Object.keys(excelData)?.[0]]?.sheetName; // 基本的にfileNameは同じはずなので最初の1つ目を取得
  if (fileName === undefined || sheetName === undefined) {
    inputErrorObj.push({
      key: business?.businessId,
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: '',
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: '',
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '',
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ファイル名、又はシート名が読み込めませんでした。フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  const errorMessages: [string, string][] = [];

  for (const propertyName of Object.keys(propertyNames)) {
    const cellNumber = excelData[propertyNames[propertyName]]?.address;

    // NOTE: 送り先名、売上先名は必須ではないため、空の場合はエラーにしない
    if (cellNumber === undefined && propertyName !== '送り先CD' && propertyName !== '売上先CD') {
      errorMessages.push([`${propertyName}が読み込めませんでした。フォーマットを再度確認し、修正してください。`, '']);
      continue;
    }

    const cellNum = cellNumber?.toString() ?? '';

    if (propertyName === '置場CD') {
      if (business?.warehouseNumber === '') {
        errorMessages.push(['置場CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.warehouseNumber.length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '商品CD') {
      if (business?.businessNumber === '') {
        errorMessages.push(['商品CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.businessNumber.length > 40) {
        errorMessages.push(['商品CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '仕入先CD') {
      if (business?.supplierNumber === '') {
        errorMessages.push(['仕入先CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.supplierNumber.length > 40) {
        errorMessages.push(['仕入先CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '売上先CD') {
      if ((business?.customerNumber ?? '').length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '送り先CD') {
      if ((business?.salesDestinationNumber ?? '').length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '容量') {
      if (business?.package === '') {
        errorMessages.push(['容量が空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (isNaN(Number(business?.package))) {
        errorMessages.push(['容量が数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    errorMessages.forEach(([errorMessage, errorCellNumber]) => {
      inputErrorObj.push({
        key: business?.businessId,
        errCode: 'InputExcelInCorrectType',
        errorFileName: {
          inputFileName: fileName.toString(),
          registerFileName: '',
        },
        preCellNumber: '',
        errorSheetName: {
          inputSheetName: sheetName.toString(),
          registerSheetName: '',
        },
        errorCellNumber: {
          inputCellNumber: errorCellNumber,
          registerCellNumber: '',
        },
        value: undefined,
        errorString: errorMessage,
      });
    });
  }
};

/**
 * 入力エクセルの内示情報のパラメータが正しいかを確認する
 * @param announce - 内示情報
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param cellAddress - セルアドレス
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateInputAnnounceParameters = (
  announce: IAnnounce,
  fileName: string,
  sheetName: string,
  cellAddress: string,
  inputErrorObj: ExcelTranslateError[]
) => {
  const dateStr = announce.year + '/' + announce.month;
  const errorMessages: string[] = [];

  if (!dateStr.match(/\d{4}\/\d{1,2}/)) {
    errorMessages.push('内示の年月が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  if (isNaN(announce.amount)) {
    errorMessages.push('内示の数量が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  errorMessages.forEach((errorMessage) => {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * 入力エクセルの日別内示情報のパラメータが正しいかを確認する
 * @param announce - 日別内示情報
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param cellAddress - セルアドレス
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateInputAnnounceDailyParameters = (
  announce: IAnnounceDaily,
  fileName: string,
  sheetName: string,
  cellAddress: string,
  inputErrorObj: ExcelTranslateError[]
) => {
  const dateStr = announce.year + '/' + announce.month + '/' + announce.day;
  const errorMessages: string[] = [];

  if (!dateStr.match(/\d{4}\/\d{1,2}\/\d{1,2}/)) {
    errorMessages.push('日別内示の年月日が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  if (isNaN(announce.amount)) {
    errorMessages.push('日別内示の数量が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  errorMessages.forEach((errorMessage) => {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * 入力エクセルの販売実績情報のパラメータが正しいかを確認する
 * @param salesResultValue - 販売実績の数量
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param cellAddress - セルアドレス
 * @param isDateErrorRow - 日付が正しくないエラーを出力するかどうか
 * @param isNotFoundTranslateRow - 転記先が存在しないエラーを出力するかどうか
 * @param isMatchRow - 転記するべき行かどうか
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateInputSalesResultsParameters = (
  salesResultValue: number,
  fileName: string,
  sheetName: string,
  cellAddress: string,
  isDateErrorRow: boolean,
  isNotFoundTranslateRow: boolean,
  isMatchRow: boolean,
  inputErrorObj: ExcelTranslateError[]
) => {
  if (isNotFoundTranslateRow) {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '', // セルとかいう概念ないので空文字
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ヘッダーの形式が正しくありません。(実績_数量ヘッダーが存在しない)フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  if (!isMatchRow) {
    return;
  }

  if (isDateErrorRow) {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress, // セルとかいう概念ないので空文字
        registerCellNumber: '',
      },
      value: undefined,
      errorString: '販売実績の年月が正しくありません。フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  const errorMessages: string[] = [];

  if (isNaN(salesResultValue)) {
    errorMessages.push('販売実績の数量が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  errorMessages.forEach((errorMessage) => {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * 入力エクセルの入荷情報のパラメータが正しいかを確認する
 * @param arrivalValue - 入荷の数量
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param cellAddress - セルアドレス
 * @param isDateErrorRow - 日付が正しくないエラーを出力するかどうか
 * @param isNotFoundTranslateRow - 転記先が存在しないエラーを出力するかどうか
 * @param isMatchRow - 転記するべき行かどうか
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateInputArrivalParameters = (
  arrivalValue: number,
  fileName: string,
  sheetName: string,
  cellAddress: string,
  isDateErrorRow: boolean,
  isNotFoundTranslateRow: boolean,
  isMatchRow: boolean,
  inputErrorObj: ExcelTranslateError[]
) => {
  if (isNotFoundTranslateRow) {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '', // セルとかいう概念ないので空文字
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ヘッダーの形式が正しくありません。(実績_数量ヘッダーが存在しない)フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  if (!isMatchRow) {
    return;
  }

  if (isDateErrorRow) {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress, // セルとかいう概念ないので空文字
        registerCellNumber: '',
      },
      value: undefined,
      errorString: '入荷の年月が正しくありません。フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  const errorMessages: string[] = [];

  if (isNaN(arrivalValue)) {
    errorMessages.push('入荷の数量が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  errorMessages.forEach((errorMessage) => {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * 入力エクセルの在庫情報のパラメータが正しいかを確認する
 * @param stockValue - 在庫の数量
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param cellAddress - セルアドレス
 * @param isDateErrorRow - 日付が正しくないエラーを出力するかどうか
 * @param isNotFoundTranslateRow - 転記先が存在しないエラーを出力するかどうか
 * @param isMatchRow - 転記するべき行かどうか
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateInputStockParameters = (
  stockValue: number,
  fileName: string,
  sheetName: string,
  cellAddress: string,
  isDateErrorRow: boolean,
  isNotFoundTranslateRow: boolean,
  isMatchRow: boolean,
  inputErrorObj: ExcelTranslateError[]
) => {
  if (isNotFoundTranslateRow) {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '', // セルとかいう概念ないので空文字
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ヘッダーの形式が正しくありません。(ロット在庫数量ヘッダーが存在しない)フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  if (!isMatchRow) {
    return;
  }

  if (isDateErrorRow) {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress, // セルとかいう概念ないので空文字
        registerCellNumber: '',
      },
      value: undefined,
      errorString:
        '在庫の年月が正しくありません。カレンダ年月ヘッダに正しいフォーマットで入力されているかどうか確認して、修正してください。',
    });
    return;
  }

  const errorMessages: string[] = [];

  if (isNaN(stockValue)) {
    errorMessages.push('在庫の数量が正しくありません。フォーマットを再度確認し、修正してください。');
  }

  errorMessages.forEach((errorMessage) => {
    inputErrorObj.push({
      key: '',
      errCode: 'InputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName,
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName,
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: cellAddress,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * 出力エクセルの基本情報のパラメータが正しいかを確認する
 * @param business - 取引情報
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param excelData - エクセルデータ
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateOutputFoundationParameters = (
  business: IBusiness,
  fileName: string,
  sheetName: string,
  excelData: ExcelOutputRow,
  inputErrorObj: ExcelTranslateError[]
) => {
  if (fileName === undefined || sheetName === undefined) {
    inputErrorObj.push({
      key: business?.businessId,
      errCode: 'OutputExcelInCorrectType',
      errorFileName: {
        inputFileName: '',
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: '',
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '',
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ファイル名、又はシート名が読み込めませんでした。フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  const propertyNames = [
    '置場CD',
    '置場名',
    '商品CD',
    '商品名',
    '容量',
    '送り先CD',
    '送り先名',
    '売上先CD',
    '売上先名',
    '仕入先CD',
    '仕入先名',
    '単位',
    'リードタイム',
    'リードタイム単位',
    '標準在庫指数(min)',
    '標準在庫指数(max)',
    '標準在庫指数単位',
    'MOQ',
  ];

  const errorMessages: [string, string][] = [];

  for (const propertyName of propertyNames) {
    const cellNumber = excelData[propertyName]?.address;

    // NOTE: 単位、送り先名、売上先名、MOQは必須ではないため、空の場合はエラーにしない
    if (
      cellNumber === undefined &&
      propertyName !== '単位' &&
      propertyName !== '送り先名' &&
      propertyName !== '売上先名' &&
      propertyName !== '送り先CD' &&
      propertyName !== '売上先CD' &&
      propertyName !== 'MOQ'
    ) {
      errorMessages.push([`${propertyName}が読み込めませんでした。フォーマットを再度確認し、修正してください。`, '']);
      continue;
    }

    const cellNum = cellNumber?.toString() ?? '';
    if (propertyName === '置場CD') {
      if (business?.warehouseNumber === '') {
        errorMessages.push(['置場CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.warehouseNumber.length > 40) {
        errorMessages.push(['置場CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '商品CD') {
      if (business?.businessNumber === '') {
        errorMessages.push(['商品CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.businessNumber.length > 40) {
        errorMessages.push(['商品CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '仕入先CD') {
      if (business?.supplierNumber === '') {
        errorMessages.push(['仕入先CDが空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.supplierNumber.length > 40) {
        errorMessages.push(['仕入先CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '売上先CD') {
      // NOTE: 売上先CDは必須ではないため、空の場合はエラーにしない
      if ((business?.customerNumber ?? '').length > 40) {
        errorMessages.push(['売上先CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '送り先CD') {
      // NOTE: 送り先CDは必須ではないため、空の場合はエラーにしない
      if ((business?.salesDestinationNumber ?? '').length > 40) {
        errorMessages.push(['送り先CDが40文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    // TODO: 容量に関しては、0はNgとする
    if (propertyName === '容量') {
      if (business?.package === '') {
        errorMessages.push(['容量が空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (isNaN(Number(business?.package))) {
        errorMessages.push(['容量が数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    if (propertyName === '商品名') {
      if (!business?.businessName) {
        errorMessages.push(['商品名が空です。フォーマットを再度確認し、修正してください。', cellNum]);
      } else {
        if (business?.businessName.length > 132) {
          errorMessages.push(['商品名が132文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
        }
      }
    }

    if (propertyName === '置場名') {
      if (business.warehouse === '') {
        errorMessages.push(['置場名が空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.warehouse.length > 255) {
        errorMessages.push([`${propertyName}が255文字を超えています。フォーマットを再度確認し、修正してください。`, cellNum]);
      }
    }

    if (propertyName === '仕入先名') {
      if (business?.supplierName === '') {
        errorMessages.push(['仕入先名が空です。フォーマットを再度確認し、修正してください。', cellNum]);
      }
      if (business?.supplierName.length > 255) {
        errorMessages.push([`${propertyName}が255文字を超えています。フォーマットを再度確認し、修正してください。`, cellNum]);
      }
    }

    if (propertyName === '送り先名') {
      // NOTE: 送り先名、売上先名は必須ではないため、空の場合はエラーにしない
      if ((business?.salesDestinationName ?? '').length > 255) {
        errorMessages.push([`${propertyName}が255文字を超えています。フォーマットを再度確認し、修正してください。`, cellNum]);
      }
    }

    if (propertyName === '売上先名') {
      // NOTE: 送り先名、売上先名は必須ではないため、空の場合はエラーにしない
      if ((business?.salesDestinationName ?? '').length > 255) {
        errorMessages.push([`${propertyName}が255文字を超えています。フォーマットを再度確認し、修正してください。`, cellNum]);
      }
    }

    if (propertyName === '単位') {
      // NOTE: 単位は必須ではないため、空の場合はエラーにしない
      if ((business?.unit ?? '').length > 6) {
        errorMessages.push(['単位が6文字を超えています。フォーマットを再度確認し、修正してください。', cellNum]);
      }
    }

    // 集計行の時のみ、リードタイム、リードタイム単位、標準在庫指数(min/max)、標準在庫指数単位、MOQについてのチェックを行う
    if (filterStringNumberUndefinedToString(excelData['集計']?.value) !== '') {
      if (propertyName === 'リードタイム') {
        if (!business?.leadTime) {
          errorMessages.push(['リードタイムが空です。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (isNaN(business?.leadTime ?? NaN)) {
          errorMessages.push(['リードタイムが数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
        }
      }

      if (propertyName === 'リードタイム単位') {
        const leadTimeUnitArr = ['日', '週', 'ヶ月', 'day', 'week', 'month'];
        if (!business?.leadTimeUnit) {
          errorMessages.push(['リードタイム単位が空です。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (!leadTimeUnitArr.includes(business?.leadTimeUnit ?? '')) {
          errorMessages.push(['リードタイム単位が日、週、ヶ月以外です。フォーマットを再度確認し、修正してください。', cellNum]);
        }
      }

      if (propertyName === '標準在庫指数(min)') {
        const stdIndexMin = business?.stdInventoryIndices?.['標準在庫指数']?.min;
        if (!stdIndexMin) {
          errorMessages.push(['標準在庫指数(min)が空です。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (isNaN(stdIndexMin ?? NaN)) {
          errorMessages.push(['標準在庫指数(min)が数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (!stdIndexMin.isDecimal(5, 2)) {
          // NOTE: DBの定義に合わせてる
          errorMessages.push([
            '標準在庫指数(min)が整数部分5桁以下、小数点以下2桁までの数字ではありません。フォーマットを再度確認し、修正してください。',
            cellNum,
          ]);
        }
      }

      if (propertyName === '標準在庫指数(max)') {
        const stdIndexMax = business?.stdInventoryIndices?.['標準在庫指数']?.max;
        if (!stdIndexMax) {
          errorMessages.push(['標準在庫指数(max)が空です。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (isNaN(stdIndexMax ?? NaN)) {
          errorMessages.push(['標準在庫指数(max)が数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (!stdIndexMax.isDecimal(5, 2)) {
          // NOTE: DBの定義に合わせてる
          errorMessages.push([
            '標準在庫指数(max)が整数部分5桁以下、小数点以下2桁までの数字ではありません。フォーマットを再度確認し、修正してください。',
            cellNum,
          ]);
        }
      }

      if (propertyName === '標準在庫指数単位') {
        const stdIndexUnitArr = ['日', '週', 'ヶ月', 'day', 'week', 'month'];
        const stdIndexUnit = business?.stdInventoryIndices?.['標準在庫指数']?.option.unit;
        if (!stdIndexUnit) {
          errorMessages.push(['標準在庫指数単位が空です。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (!stdIndexUnitArr.includes(stdIndexUnit)) {
          errorMessages.push(['標準在庫指数単位が日、週、ヶ月以外です。フォーマットを再度確認し、修正してください。', cellNum]);
        }
      }

      // NOTE: MOQは必須ではないため、空の場合はエラーにしない
      if (propertyName === 'MOQ' && business?.MOQ != null) {
        const moq = business?.MOQ;
        if (isNaN(moq)) {
          errorMessages.push(['MOQが数字ではありません。フォーマットを再度確認し、修正してください。', cellNum]);
        } else if (!moq.isDecimal(18, 6)) {
          // NOTE: DBの定義に合わせてる
          errorMessages.push([
            'MOQが整数部分18桁以下、小数点以下6桁までの数字ではありません。フォーマットを再度確認し、修正してください。',
            cellNum,
          ]);
        }
      }
    }
  }

  errorMessages.forEach(([errorMessage, errorCellNumber]) => {
    inputErrorObj.push({
      key: business?.businessId,
      errCode: 'OutputExcelInCorrectType',
      errorFileName: {
        inputFileName: fileName.toString(),
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: sheetName.toString(),
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: errorCellNumber,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * 出力エクセルのCDとNameに差異が無いかを確認する
 * @param business - 取引情報
 * @param differenceConfirmationCodeAndNameObj - CDとNameの差分確認用のオブジェクト
 * @param fileName - ファイル名
 * @param sheetName - シート名
 * @param excelData - エクセルデータ
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateOutputExcelDifferenceConfirmationCodeAndName = (
  business: IBusiness,
  differenceConfirmationCodeAndNameObj: DifferenceConfirmationCodeAndName,
  fileName: string,
  sheetName: string,
  excelData: ExcelOutputRow,
  inputErrorObj: ExcelTranslateError[]
) => {
  if (fileName === undefined || sheetName === undefined) {
    inputErrorObj.push({
      key: business?.businessId,
      errCode: 'OutputExcelInCorrectType',
      errorFileName: {
        inputFileName: '',
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: '',
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '',
        registerCellNumber: '',
      },
      value: undefined,
      errorString: 'ファイル名、又はシート名が読み込めませんでした。フォーマットを再度確認し、修正してください。',
    });
    return;
  }

  const propertyNames = ['仕入先名', '置場名', '商品名', '売上先名'];

  // プロパティ名ごとに空オブジェクトを入れておく
  for (const propertyName of propertyNames) {
    if (!differenceConfirmationCodeAndNameObj[propertyName]) differenceConfirmationCodeAndNameObj[propertyName] = {};
  }

  // NOTE: [errorMassage, preCellNum, cellNum]
  const errorMessages: [string, string, string][] = [];

  for (const propertyName of propertyNames) {
    const cellNumber = excelData[propertyName]?.address;
    if (cellNumber === undefined && propertyName !== '売上先名') {
      errorMessages.push([`${propertyName}が読み込めませんでした。フォーマットを再度確認し、修正してください。`, '', '']);
      continue;
    }

    const cellNum = cellNumber?.toString() ?? '';

    if (propertyName === '仕入先名') {
      // 差異チェック用のオブジェクトに置場CDに紐づく情報が存在しないなら入れる
      if (!differenceConfirmationCodeAndNameObj[propertyName][business?.supplierNumber]) {
        differenceConfirmationCodeAndNameObj[propertyName][business?.supplierNumber] = {
          name: business?.supplierName,
          cellNumber: cellNum,
        };
      } else {
        // 差異チェック用のオブジェクトに同一の仕入先CDがある&仕入先名が異なる場合エラーとする
        if (differenceConfirmationCodeAndNameObj[propertyName][business?.supplierNumber].name !== business?.supplierName) {
          errorMessages.push([
            '同一仕入先CDですが、仕入先名が異なる情報が含まれています。',
            String(differenceConfirmationCodeAndNameObj[propertyName][business?.supplierNumber].cellNumber),
            cellNum,
          ]);
        }
      }
    }

    if (propertyName === '置場名') {
      // 差異チェック用のオブジェクトに置場CDに紐づく情報が存在しないなら入れる
      if (!differenceConfirmationCodeAndNameObj[propertyName][business?.warehouseNumber]) {
        differenceConfirmationCodeAndNameObj[propertyName][business?.warehouseNumber] = {
          name: business?.warehouse,
          cellNumber: cellNum,
        };
      } else {
        // 差異チェック用のオブジェクトに同一の置場CDがある&置場名が異なる場合エラーとする
        if (differenceConfirmationCodeAndNameObj[propertyName][business?.warehouseNumber].name !== business?.warehouse) {
          errorMessages.push([
            '同一置場CDですが、置場名が異なる情報が含まれています。',
            String(differenceConfirmationCodeAndNameObj[propertyName][business?.warehouseNumber].cellNumber),
            cellNum,
          ]);
        }
      }
    }

    if (propertyName === '商品名') {
      // 差異チェック用のオブジェクトに商品CDに紐づく情報が存在しないなら入れる
      if (!differenceConfirmationCodeAndNameObj[propertyName][business?.businessNumber]) {
        differenceConfirmationCodeAndNameObj[propertyName][business?.businessNumber] = {
          name: business?.businessName ?? '',
          cellNumber: cellNum,
        };
      } else {
        // 差異チェック用のオブジェクトに同一の商品CDがある&商品名が異なる場合エラーとする
        if (differenceConfirmationCodeAndNameObj[propertyName][business?.businessNumber].name !== (business?.businessName ?? '')) {
          errorMessages.push([
            '同一商品CDですが、商品名が異なる情報が含まれています。',
            String(differenceConfirmationCodeAndNameObj[propertyName][business?.businessNumber].cellNumber),
            cellNum,
          ]);
        }
      }
    }

    if (propertyName === '売上先名') {
      // 差異チェック用のオブジェクトに売上先CDに紐づく情報が存在しないなら入れる
      if (business?.customerNumber) {
        if (!differenceConfirmationCodeAndNameObj[propertyName][business?.customerNumber]) {
          differenceConfirmationCodeAndNameObj[propertyName][business?.customerNumber] = {
            name: business?.customerName ?? '',
            cellNumber: cellNum,
          };
        } else {
          // 差異チェック用のオブジェクトに同一の売上先CDがある&売上先名が異なる場合エラーとする
          if (differenceConfirmationCodeAndNameObj[propertyName][business?.customerNumber].name !== (business?.customerName ?? '')) {
            errorMessages.push([
              '同一売上先CDですが、売上先名が異なる情報が含まれています。',
              String(differenceConfirmationCodeAndNameObj[propertyName][business?.customerNumber].cellNumber),
              cellNum,
            ]);
          }
        }
      }
    }
  }

  errorMessages.forEach(([errorMessage, preCellNumber, errorCellNumber]) => {
    inputErrorObj.push({
      key: business?.businessId,
      errCode: 'OutputExcelDifferenceCodeAndNameType',
      errorFileName: {
        inputFileName: fileName.toString(),
        registerFileName: '',
      },
      preCellNumber: preCellNumber,
      errorSheetName: {
        inputSheetName: sheetName.toString(),
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: errorCellNumber,
        registerCellNumber: '',
      },
      value: undefined,
      errorString: errorMessage,
    });
  });
};

/**
 * xxxxエクセルを入力したのに1つもxxxxの情報が取れなかった場合のエラーを検出する
 * @param businesses - 取引情報
 * @param successTranslateKeys - 取引情報のキー
 * @param inputFileTypes - 入力エクセルの種類
 * @param inputErrorObj - エラー情報を格納するオブジェクト（エラーがあれば追加）
 */
export const validateFailedInputExcel = (
  businesses: Record<string, TranslateExcelBusiness>,
  successTranslateKeys: Record<string, boolean>,
  inputFileTypes: InputExcelType[],
  inputErrorObj: ExcelTranslateError[]
) => {
  const translateSuccessFileTypesSet: Set<InputExcelType> = new Set();
  const translateSuccessBusinesses = Object.entries(businesses)
    .map(([key, value]) => {
      if (successTranslateKeys[key]) {
        return value;
      }
    })
    .filter((business) => business !== undefined);

  translateSuccessBusinesses.forEach((business) => {
    Object.values(InputExcelType).forEach((inputFileType) => {
      switch (inputFileType) {
        case '入荷':
          if ((business?.arrivals?.length ?? 0) > 0) {
            translateSuccessFileTypesSet.add(inputFileType);
          }
          break;
        case '内示':
          if ((business?.customerAnnounce?.length ?? 0) > 0) {
            translateSuccessFileTypesSet.add(inputFileType);
          }
          break;
        case '日別内示':
          if ((business?.customerAnnounceDaily?.length ?? 0) > 0) {
            translateSuccessFileTypesSet.add(inputFileType);
          }
          break;
        case '在庫':
          if ((business?.mvpInventory?.length ?? 0) > 0) {
            translateSuccessFileTypesSet.add(inputFileType);
          }
          break;
        case '販売実績':
          if ((business?.salesResults?.length ?? 0) > 0) {
            translateSuccessFileTypesSet.add(inputFileType);
          }
          break;
        case '発注取込':
          // 発注取込については一旦スキップ
          break;
        default:
          assertNever(inputFileType);
      }
    });
  });

  const errorFileTypes: string[] = [];
  inputFileTypes.forEach((fileType) => {
    switch (fileType) {
      case '入荷':
        if (!translateSuccessFileTypesSet.has('入荷')) {
          errorFileTypes.push('入荷');
        }
        break;
      case '内示':
        if (!translateSuccessFileTypesSet.has('内示')) {
          errorFileTypes.push('内示');
        }
        break;
      case '日別内示':
        if (!translateSuccessFileTypesSet.has('日別内示')) {
          errorFileTypes.push('日別内示');
        }
        break;
      case '在庫':
        if (!translateSuccessFileTypesSet.has('在庫')) {
          errorFileTypes.push('在庫');
        }
        break;
      case '販売実績':
        if (!translateSuccessFileTypesSet.has('販売実績')) {
          errorFileTypes.push('販売実績');
        }
        break;
      case '発注取込':
        // 発注取込については一旦スキップ
        break;
      default:
        assertNever(fileType);
    }
  });

  errorFileTypes.forEach((errorFileType) => {
    inputErrorObj.push({
      key: '',
      errCode: 'FailedInputExcel',
      errorFileName: {
        inputFileName: '',
        registerFileName: '',
      },
      preCellNumber: '',
      errorSheetName: {
        inputSheetName: '',
        registerSheetName: '',
      },
      errorCellNumber: {
        inputCellNumber: '',
        registerCellNumber: '',
      },
      value: undefined,
      errorString: `${errorFileType}`,
    });
  });
};
